import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { robotsMeta } from "@/lib/seo";
import {
  crmPublicObtenerPortafolio,
  crmPublicConfirmarSeleccion,
} from "@/server/crm/portafolio-publico.fns";
import type { PortafolioPublicoDTO, TipoServicioRequerimiento } from "@/lib/crm/types";
import { PortafolioHeader } from "@/components/portafolio/PortafolioHeader";
import { CompCardDestacada } from "@/components/portafolio/CompCardDestacada";
import { RosterGrid } from "@/components/portafolio/RosterGrid";
import { PortafolioLightbox } from "@/components/portafolio/PortafolioLightbox";
import { SeleccionBar } from "@/components/portafolio/SeleccionBar";
import { PortafolioVencido } from "@/components/portafolio/PortafolioVencido";

type LoaderData = PortafolioPublicoDTO | { estado: "invalido" };

export const Route = createFileRoute("/portafolio")({
  validateSearch: (search) => ({ t: typeof search.t === "string" ? search.t : "" }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ deps }): Promise<LoaderData> => {
    if (!deps.t) return { estado: "invalido" };
    try {
      return await crmPublicObtenerPortafolio({ data: { token: deps.t } });
    } catch {
      return { estado: "invalido" };
    }
  },
  head: ({ loaderData }) => {
    const activo = loaderData && loaderData.estado === "activo";
    const titulo = activo
      ? `Portafolio — ${loaderData.requerimiento.titulo} | Improve Yourself`
      : "Enlace vencido — Improve Yourself";
    return { meta: [{ title: titulo }, ...robotsMeta(false, false)] };
  },
  component: PortafolioPage,
});

function PortafolioPage() {
  const data = Route.useLoaderData();
  const { t } = Route.useSearch();
  const qc = useQueryClient();

  const [destacadoId, setDestacadoId] = useState<string | null>(
    data.estado === "activo" ? (data.perfiles[0]?.convocatoriaId ?? null) : null,
  );
  const [lightbox, setLightbox] = useState<{ perfilId: string; index: number } | null>(null);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(
    new Set(data.estado === "activo" ? (data.seleccion?.convocatoriaIds ?? []) : []),
  );
  const [confirmarError, setConfirmarError] = useState<string | null>(null);
  const [seleccionConfirmada, setSeleccionConfirmada] = useState(false);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const confirmarMutation = useMutation({
    mutationFn: (v: { nombreCliente: string; comentario: string; turnstileToken: string }) =>
      crmPublicConfirmarSeleccion({
        data: {
          token: t,
          convocatoria_ids: [...seleccionados],
          nombre_cliente: v.nombreCliente,
          comentario: v.comentario || undefined,
          turnstileToken: v.turnstileToken,
        },
      }),
    onSuccess: () => {
      setConfirmarError(null);
      setSeleccionConfirmada(true);
      qc.invalidateQueries({ queryKey: ["portafolio-publico", t] });
    },
    onError: (e: Error) => {
      setConfirmarError(e.message);
      // Turnstile tokens son de un solo uso; un reintento necesita un token nuevo.
      setTurnstileResetKey((key) => key + 1);
    },
  });

  // `perfiles` se calcula antes de cualquier return condicional: los hooks de
  // abajo (useMemo) deben llamarse siempre en el mismo orden, sin importar el
  // estado del portafolio (regla de hooks de React).
  const perfiles = useMemo(() => (data.estado === "activo" ? data.perfiles : []), [data]);

  const fotosLightbox = useMemo(() => {
    if (!lightbox) return [];
    const perfil = perfiles.find((p) => p.convocatoriaId === lightbox.perfilId);
    return (perfil?.fotos ?? []).map((f) => ({
      url: f.url,
      thumbUrl: f.thumbUrl,
      focoX: f.focoX,
      focoY: f.focoY,
    }));
  }, [lightbox, perfiles]);

  if (data.estado !== "activo") {
    return <PortafolioVencido estado={data.estado === "invalido" ? "invalido" : data.estado} />;
  }

  const destacado = perfiles.find((p) => p.convocatoriaId === destacadoId) ?? perfiles[0];
  const seleccionEnviada = data.seleccion !== null;
  const seleccionCerrada = data.seleccionCerrada || seleccionEnviada;
  // El estado "activo" ya garantiza el shape completo de `requerimiento`
  // (construirPortafolioPublico lo reduce a solo {titulo} en cualquier otro
  // estado) — TS no puede enlazar ambos campos discriminados por separado.
  const requerimiento = data.requerimiento as {
    titulo: string;
    tipoServicio: TipoServicioRequerimiento;
    cantidad: number;
    fechaInicio: string;
    fechaFin: string;
    horario: string | null;
    distrito: string | null;
    clienteNombreVisible: string | null;
    marcaNombreVisible: string | null;
    marcaLogoUrl: string | null;
  };

  function toggleSeleccion(convocatoriaId: string) {
    if (seleccionCerrada) return;
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(convocatoriaId)) next.delete(convocatoriaId);
      else next.add(convocatoriaId);
      return next;
    });
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] pb-24">
      <PortafolioHeader
        clienteNombreVisible={requerimiento.clienteNombreVisible}
        marcaNombreVisible={requerimiento.marcaNombreVisible}
        marcaLogoUrl={requerimiento.marcaLogoUrl}
        tipoServicio={requerimiento.tipoServicio}
        fechaInicio={requerimiento.fechaInicio}
        fechaFin={requerimiento.fechaFin}
        expiraEn={data.expiraEn}
      />

      <main className="mx-auto max-w-6xl space-y-10 px-4 py-8 sm:px-6">
        {destacado && (
          <CompCardDestacada
            perfil={destacado}
            orden={perfiles.findIndex((p) => p.convocatoriaId === destacado.convocatoriaId) + 1}
            total={perfiles.length}
            seleccionado={seleccionados.has(destacado.convocatoriaId)}
            seleccionCerrada={seleccionCerrada}
            onToggleSeleccion={() => toggleSeleccion(destacado.convocatoriaId)}
            onAbrirLightbox={(index) => setLightbox({ perfilId: destacado.convocatoriaId, index })}
          />
        )}

        {perfiles.length > 1 && (
          <RosterGrid
            perfiles={perfiles}
            destacadoId={destacado?.convocatoriaId ?? ""}
            seleccionados={seleccionados}
            seleccionCerrada={seleccionCerrada}
            onVerFicha={(id) => {
              setDestacadoId(id);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onToggleSeleccion={toggleSeleccion}
          />
        )}
      </main>

      {lightbox && fotosLightbox.length > 0 && (
        <PortafolioLightbox
          fotos={fotosLightbox}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(index) => setLightbox((prev) => (prev ? { ...prev, index } : prev))}
        />
      )}

      <SeleccionBar
        seleccionCount={seleccionados.size}
        cantidadSolicitada={requerimiento.cantidad}
        seleccionCerrada={seleccionCerrada}
        seleccionEnviada={seleccionEnviada}
        pending={confirmarMutation.isPending}
        error={confirmarError}
        confirmada={seleccionConfirmada}
        turnstileResetKey={turnstileResetKey}
        onConfirmar={(v) => confirmarMutation.mutate(v)}
      />
    </div>
  );
}
